import { cn } from "@/lib/utils";

interface ConexusWordmarkProps {
  className?: string;
  variant?: "gradient" | "navy" | "white" | "black" | "blue-gradient";
  showSubtitle?: boolean;
  size?: "sm" | "md" | "lg" | "xl";
  centered?: boolean;
}

export const ConexusWordmark = ({ 
  className, 
  variant = "gradient",
  showSubtitle = true,
  size = "md",
  centered = false
}: ConexusWordmarkProps) => {
  const gradientId = `wordmark-gradient-${Math.random().toString(36).substr(2, 9)}`;
  const blueGradientId = `wordmark-blue-gradient-${Math.random().toString(36).substr(2, 9)}`;
  const navyGradientId = `wordmark-navy-gradient-${Math.random().toString(36).substr(2, 9)}`;
  
  const sizeConfig = {
    sm: { width: 120, mainSize: 24, subSize: 6 },
    md: { width: 180, mainSize: 36, subSize: 9 },
    lg: { width: 240, mainSize: 48, subSize: 12 },
    xl: { width: 320, mainSize: 64, subSize: 16 },
  };

  const config = sizeConfig[size];
  const height = showSubtitle ? config.mainSize * 1.5 : config.mainSize;

  const getColor = () => {
    switch (variant) {
      case "navy":
        return `url(#${navyGradientId})`;
      case "white":
        return "white";
      case "black":
        return "#1a1a1a";
      case "blue-gradient":
        return `url(#${blueGradientId})`;
      default:
        return `url(#${gradientId})`;
    }
  };

  const getSubtitleColor = () => {
    switch (variant) {
      case "navy":
        return "#0c2d4f";
      case "white":
        return "rgba(255,255,255,0.8)";
      case "black":
        return "#404040";
      case "blue-gradient":
        return "#1e40af";
      default:
        return "hsl(var(--conexus-navy))";
    }
  };

  const textX = centered ? config.width / 2 : 0;
  const textAnchor = centered ? "middle" : "start";

  return (
    <svg
      width={config.width}
      height={height}
      viewBox={`0 0 ${config.width} ${height}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={cn("conexus-wordmark", className)}
    >
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="hsl(var(--conexus-orange))" />
          <stop offset="50%" stopColor="hsl(var(--conexus-amber))" />
          <stop offset="100%" stopColor="hsl(var(--conexus-gold))" />
        </linearGradient>
        <linearGradient id={blueGradientId} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#1e40af" />
          <stop offset="50%" stopColor="#3b82f6" />
          <stop offset="100%" stopColor="#60a5fa" />
        </linearGradient>
        <linearGradient id={navyGradientId} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#0a1f3a" />
          <stop offset="50%" stopColor="#0c2d4f" />
          <stop offset="100%" stopColor="#1a4a7a" />
        </linearGradient>
      </defs>
      
      <text
        x={textX}
        y={config.mainSize * 0.8}
        fill={getColor()}
        fontFamily="Poppins, sans-serif"
        fontSize={config.mainSize}
        fontWeight="600"
        letterSpacing="-0.02em"
        textAnchor={textAnchor}
      >
        conexus
      </text>
      
      {showSubtitle && (
        <text
          x={textX}
          y={config.mainSize + config.subSize + 4}
          fill={getSubtitleColor()}
          fontFamily="Poppins, sans-serif"
          fontSize={config.subSize}
          fontWeight="500"
          letterSpacing="0.2em"
          opacity="0.85"
          textAnchor={textAnchor}
        >
          GLOBAL CONSULTING
        </text>
      )}
    </svg>
  );
};

export default ConexusWordmark;
